import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section
      className="relative min-h-screen overflow-hidden flex items-center"
      style={{ backgroundColor: "var(--bg-base)" }}
    >
      {/* Background grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(var(--grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Glow orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ backgroundColor: "var(--glow-primary)", filter: "blur(140px)" }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ backgroundColor: "var(--glow-secondary)", filter: "blur(120px)" }}
      />

      <div className="relative w-full max-w-7xl mx-auto px-6 pt-28 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div>
            <div
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border"
              style={{
                backgroundColor: "rgba(0, 210, 168, 0.10)",
                borderColor: "rgba(0, 210, 168, 0.20)",
              }}
            >
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "var(--accent)" }} />
              <span className="text-xs font-medium tracking-wide" style={{ color: "var(--accent)" }}>
                {t("hero.badge")}
              </span>
            </div>

            <h1
              className="text-5xl md:text-6xl font-extrabold leading-tight mb-6"
              style={{ color: "var(--text-primary)" }}
            >
              {t("hero.title1")}{" "}
              <span style={{ color: "var(--accent)" }}>{t("hero.title2")}</span>
            </h1>

            <p className="text-base leading-relaxed mb-8 max-w-lg" style={{ color: "var(--text-secondary)" }}>
              {t("hero.desc")}
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <button
                onClick={() => navigate("/download/android")}
                className="flex items-center gap-2 font-semibold text-sm px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: "var(--accent)", color: "var(--accent-text)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--accent-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--accent)")}
              >
                <i className="ri-android-line" />
                Android {t("hero.download")}
              </button>
              <button
                onClick={() => navigate("/download/ios")}
                className="flex items-center gap-2 text-sm px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                style={{
                  borderColor: "var(--border-hover)",
                  color: "var(--text-primary)",
                  backgroundColor: "transparent",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-hover)")}
              >
                <i className="ri-apple-line" />
                iOS {t("hero.download")}
              </button>
              <button
                onClick={() => navigate("/download/pc")}
                className="flex items-center gap-2 text-sm px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                style={{
                  borderColor: "var(--border-hover)",
                  color: "var(--text-primary)",
                  backgroundColor: "transparent",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--text-primary)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border-hover)")}
              >
                <i className="ri-computer-line" />
                PC {t("hero.download")}
              </button>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-8">
              <div>
                <div className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>13k+</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>GitHub Stars</div>
              </div>
              <div className="w-px self-stretch" style={{ backgroundColor: "var(--border-color)" }} />
              <div>
                <div className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>50k+</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("hero.stat.devs")}</div>
              </div>
              <div className="w-px self-stretch" style={{ backgroundColor: "var(--border-color)" }} />
              <div>
                <div className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>1M+</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("hero.stat.msgs")}</div>
              </div>
            </div>
          </div>

          {/* Right: Phone mockups */}
          <div className="relative flex items-center justify-center">
            {/* Back phone */}
            <div
              className="absolute -right-4 top-6 w-44 h-[360px] rounded-[36px] overflow-hidden opacity-60 rotate-3 border"
              style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-color)" }}
            >
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 w-14 h-4 rounded-full z-10"
                style={{ backgroundColor: "var(--bg-base)" }}
              />
              <img
                src="https://readdy.ai/api/search-image?query=mobile%20chat%20app%20dark%20theme%20contacts%20list%20user%20avatars%20names%20clean%20modern%20instant%20messaging%20dark%20background%20green%20accent%20minimal%20design%20UI&width=176&height=360&seq=hero-phone-back&orientation=portrait"
                alt="contacts"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Front phone */}
            <div
              className="relative w-52 h-[420px] rounded-[42px] overflow-hidden z-10 border-2"
              style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border-hover)" }}
            >
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 w-16 h-5 rounded-full z-10"
                style={{ backgroundColor: "var(--bg-base)" }}
              />
              <img
                src="https://readdy.ai/api/search-image?query=mobile%20instant%20messaging%20app%20dark%20background%20green%20accent%20color%20chat%20bubbles%20messages%20UI%20clean%20modern%20design%20minimal%20chat%20interface%20with%20message%20list&width=208&height=416&seq=hero-phone-front&orientation=portrait"
                alt="chat"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full" style={{ backgroundColor: "var(--text-muted)" }} />
            </div>

            {/* Floating badge */}
            <div
              className="absolute -left-6 bottom-16 rounded-2xl px-4 py-3 z-20 border"
              style={{
                backgroundColor: "var(--bg-surface)",
                borderColor: "rgba(0, 210, 168, 0.30)",
              }}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="w-8 h-8 flex items-center justify-center rounded-xl"
                  style={{ backgroundColor: "rgba(0, 210, 168, 0.15)" }}
                >
                  <i className="ri-shield-keyhole-line" style={{ color: "var(--accent)" }} />
                </div>
                <div>
                  <div className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>{t("hero.badge2.title")}</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("hero.badge2.desc")}</div>
                </div>
              </div>
            </div>

            {/* Glow under phones */}
            <div
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-3/4 h-14 pointer-events-none"
              style={{ backgroundColor: "rgba(0, 210, 168, 0.10)", filter: "blur(16px)" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}